import { Icona } from '../../components/Icona';
import type { DefinizioneColonna, FiltroColonna } from '../../lib/tipi';
import { ETICHETTE_OPERATORE, ETICHETTE_PERIODO } from '../../lib/tipi';
import { formattaData, formattaNumero } from '../../lib/formato';

interface Props {
  filtri: FiltroColonna[];
  colonne: DefinizioneColonna[];
  totaleRighe: number;
  righeNonFiltrate?: number;
  onRimuovi: (colonna: string) => void;
  onAzzera: () => void;
}

/**
 * I filtri attivi restano davanti agli occhi. Righe che scompaiono senza una
 * spiegazione visibile sono il motivo per cui gli utenti smettono di fidarsi di una griglia.
 */
export function ChipFiltri({
  filtri,
  colonne,
  totaleRighe,
  righeNonFiltrate,
  onRimuovi,
  onAzzera,
}: Props) {
  if (filtri.length === 0 && righeNonFiltrate === undefined) return null;

  return (
    <div className="chip-filtri">
      {filtri.map((filtro) => {
        const colonna = colonne.find((c) => c.nome === filtro.colonna);
        return (
          <span key={filtro.colonna} className="chip">
            <b>{filtro.colonna}</b>
            <i>{ETICHETTE_OPERATORE[filtro.operatore]}</i>
            <span>{descriviValori(filtro, colonna)}</span>
            <button
              type="button"
              aria-label={`Rimuovi il filtro su ${filtro.colonna}`}
              onClick={() => onRimuovi(filtro.colonna)}
            >
              <Icona nome="rimuovi" />
            </button>
          </span>
        );
      })}

      {filtri.length > 0 && (
        <button type="button" className="azzera" onClick={onAzzera}>
          Azzera tutto
        </button>
      )}

      <span className="conteggio">{etichettaConteggio(totaleRighe, righeNonFiltrate, filtri.length > 0)}</span>
    </div>
  );
}

function descriviValori(filtro: FiltroColonna, colonna?: DefinizioneColonna): string {
  const valori = filtro.valori.filter((v): v is string => v !== null && v !== '');

  if (filtro.operatore === 'PeriodoCorrente') {
    const periodo = valori[0];
    return periodo ? (ETICHETTE_PERIODO[periodo as keyof typeof ETICHETTE_PERIODO] ?? periodo) : '';
  }

  if (filtro.operatore === 'EUnoDi' || filtro.operatore === 'NonEUnoDi') {
    return valori.length <= 2 ? valori.join(', ') : `${valori.length} valori`;
  }

  const formattati =
    colonna?.tipo === 'Data' ? valori.map((v) => formattaData(v)) : valori;

  return formattati.join(' – ');
}

/*  «1 righe» e' il difetto che si legge per primo su una griglia con una sola riga, e su
    questa griglia ci si arriva subito perche' le commesse aperte sono poche. La regola
    del plurale sta qui, in una funzione pura, invece che dentro il JSX: cosi' si prova
    senza disegnare niente.  */
export function etichettaConteggio(
  totale: number,
  nonFiltrate: number | undefined,
  conFiltri: boolean,
): string {
  // Le migliaia col separatore come in ogni altra cifra dell'applicativo: «1240 righe»
  // in mezzo a una colonna di importi formattati stona e si legge peggio.
  const righe = (n: number) => `${formattaNumero(n)} ${n === 1 ? 'riga' : 'righe'}`;

  // Con dei filtri attivi conta di piu' sapere da quante si e' partiti: la freccia dice
  // «da tante a poche», ed e' il numero che fa capire se il filtro ha morso.
  return nonFiltrate !== undefined && conFiltri
    ? `${righe(nonFiltrate)} → ${formattaNumero(totale)}`
    : righe(totale);
}
